import { appLocale } from "../../locales/display.ts";
import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { DateTimeTooltip } from "src/view/conponents/DateTimeTooltip";
import { formatDisplayDate, formatDisplayTime, parseServerDate } from "src/utils/dateTime";
import Button from "@ui/button/Button";
import { ShieldAlert } from "lucide-react";
import { useState } from "react";
import styled from "styled-components";
import { GD } from "src/GD";
import type { IPaymentSession } from "src/types/types";
import { Pagination } from "../conponents/Pagination";
import type { TTone } from "../providers/providersData";
import { Cell, CopyButton, DirectoryCard, DirectoryRow, EmptyState, EntityCell, IconTile, Mono, Muted, Notice, RowChevron, SkeletonRows, StatusPill } from "../providers/providersUi";

const PER_PAGE = 20;

type TChargeback = {
	id: number;
	session_id: number;
	payment_id?: string | number | null;
	status: string;
	amount: number;
	currency: string;
	merchant_id: number;
	created_at: string | number;
};

type TChargebacksPage = {
	items: TChargeback[];
	pagination: { total: number; page: number; perPage: number; totalPages: number };
};

const STATUS: Record<string, { tone: TTone; label: string }> = {
	opened: { tone: "info", get label() { return msg("opened"); } },
	pending: { tone: "warning", get label() { return msg("pending"); } },
	completed: { tone: "positive", get label() { return msg("completed"); } },
	closed: { tone: "positive", get label() { return msg("closed"); } },
	failed: { tone: "negative", get label() { return msg("failed_031a8f0f"); } },
	error: { tone: "negative", get label() { return msg("error"); } },
};

const statusOf = (status?: string) => {
	const key = (status ?? "").toLowerCase();
	return STATUS[key] ?? { tone: "neutral" as TTone, label: key ? key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ") : msg("unknown") };
};

const dateParts = (value: string | number) => {
	const date = parseServerDate(value);
	if (!date) return { date: String(value), time: "" };
	return {
		date: formatDisplayDate(date, { locale: "en-US", format: { month: "short", day: "2-digit", year: "numeric" }, withZone: false }),
		time: formatDisplayTime(date, { format: { hour: "2-digit", minute: "2-digit" } }),
	};
};

/** The whole tab scrolls, as on Statistics and Fees; the pager stays pinned to the bottom of the tab. */
const Layout = styled.div`
	display: flex;
	flex-direction: column;
	/* At least the tab's height, so a short list still keeps the pager at the bottom. */
	flex: 1 0 auto;
	gap: 15px;
	> [data-panel="footer"] {
		position: sticky;
		bottom: 0;
		z-index: 3;
		margin-top: auto;
	}
	.amount {
		display: block;
		color: #b42318;
		font-weight: 600;
		font-variant-numeric: tabular-nums;
		text-align: right;
		white-space: nowrap;
	}
	.amount .currency {
		margin-left: 4px;
		color: #667085;
		font-weight: 400;
	}
	.ids {
		display: inline-flex;
		align-items: center;
		gap: 2px;
	}
	.right {
		display: block;
		text-align: right;
	}
	.footer {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 10px;
		margin-left: auto;
	}
`;

/** Chargebacks of one website: date, session, payment, amount and status; a row opens its payment. */
export const MerchantChargebacks = ({ merchantId }: { merchantId: number }) => {
	useLocale();
	const [page, setPage] = useState(0);
	const request = (next: number) => ({ method: "chargebacks/list", data: { merchant_id: merchantId, page: next, limit: PER_PAGE } });
	const [response, reload, _reset, busy] = GD.REQ_API.useRequest(null, request(page));
	const data = response?.data as TChargebacksPage | undefined;
	const items = Array.isArray(data?.items) ? data.items : [];
	const loading = !response || (busy && !items.length);

	const changePage = (next: number) => {
		setPage(next);
		reload(request(next));
	};

	return (
		<Layout>
			{response?.error && <Notice tone="error">{response.error.message ?? msg("couldNotLoadChargebacks")}</Notice>}
			<div>
				<DirectoryCard
					stickyHead
					columns="minmax(220px, 1fr) minmax(130px, max-content) minmax(130px, max-content) minmax(110px, max-content) minmax(110px, max-content)"
					head={["Chargeback", "Session", "Payment ID", <span className="right">{"Amount"}</span>, "Status"]}
					footer={!loading && items.length > 0 && data?.pagination && <span>{data.pagination.total}{msg("chargebacks_698edfcc")}</span>}>
					{loading ? (
						<SkeletonRows rows={5} />
					) : !items.length ? (
						!response?.error && <EmptyState icon={<ShieldAlert size={20} />} title={msg("noChargebacks")} text={msg("thereAreNoChargebacksForThisWebsite")} />
					) : (
						items.map(item => {
							const status = statusOf(item.status);
							const when = dateParts(item.created_at);
							return (
								<DirectoryRow
									key={item.id}
									label={msg("templateOpenPayment_71bec3d0", { v0: item.session_id })}
									onOpen={() => GD.S_TRANSACTION_PREVIEW.invoke({ id: item.session_id } as IPaymentSession)}>
									<Cell kind="main">
										<EntityCell
											icon={
												<IconTile>
													<ShieldAlert />
												</IconTile>
											}
											name={<DateTimeTooltip value={item.created_at}>{when.date}</DateTimeTooltip>}
											extra={when.time && <Muted style={{ fontWeight: 400 }}><DateTimeTooltip value={item.created_at}>{when.time}</DateTimeTooltip></Muted>}
											sub={
												<span className="ids">
													<Mono>#{item.id}</Mono>
													<CopyButton value={String(item.id)} label={msg("copyChargebackId")} />
												</span>
											}
										/>
									</Cell>
									<Cell label={msg("session")}>
										<span className="ids">
											<Mono>{item.session_id}</Mono>
											<CopyButton value={String(item.session_id)} label={msg("copySessionId")} />
										</span>
									</Cell>
									<Cell label={msg("paymentId_d9c9afd8")}>
										{item.payment_id != null && item.payment_id !== "" ? (
											<span className="ids">
												<Mono>{item.payment_id}</Mono>
												<CopyButton value={String(item.payment_id)} label={msg("copyPaymentId")} />
											</span>
										) : (
											<Muted>—</Muted>
										)}
									</Cell>
									<Cell label={msg("amount")}>
										<span className="amount">
											−{(Number(item.amount) / 100).toLocaleString(appLocale("en-US"), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
											<span className="currency">{item.currency}</span>
										</span>
									</Cell>
									<Cell kind="trail">
										<StatusPill tone={status.tone} label={status.label} />
										<RowChevron />
									</Cell>
								</DirectoryRow>
							);
						})
					)}
				</DirectoryCard>
			</div>
			<div data-panel="footer">
				<Pagination pagination={data?.pagination} onPageChange={changePage} disabled={busy} />
				<div className="footer">
					<Button title={msg("refresh")} variant="primarySmall" disabled={busy} onClick={() => reload(request(page))} />
				</div>
			</div>
		</Layout>
	);
};
